import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Car, Shield, Users, Sparkles } from 'lucide-react'

export default function SignUpPrompt({ isOpen, onClose, action = 'continue' }) {
  const navigate = useNavigate()

  if (!isOpen) return null

  const benefits = [
    { icon: Car, text: 'Track your entire vehicle collection' },
    { icon: Shield, text: 'Store service records & documents securely' },
    { icon: Users, text: 'Connect with fellow enthusiasts' },
    { icon: Sparkles, text: 'Access exclusive concierge services' },
  ]

  const handleSignUp = () => {
    onClose()
    navigate('/signup')
  }

  const handleSignIn = () => {
    onClose()
    navigate('/login')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-rdc-taupe hover:text-black transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="bg-gradient-to-br from-rdc-primary to-rdc-primary-dark p-8 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Car className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-display font-bold text-white mb-2">
            Join the Club
          </h2>
          <p className="text-white/90">
            Create a free account to {action}
          </p>
        </div>

        {/* Benefits */}
        <div className="p-6">
          <div className="space-y-3 mb-6">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rdc-primary/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-4 h-4 text-rdc-primary" />
                </div>
                <span className="text-sm text-rdc-dark-gray">{benefit.text}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <button
            onClick={handleSignUp}
            className="w-full py-3 bg-rdc-primary text-white font-semibold rounded-lg hover:bg-rdc-primary-dark transition-colors mb-3"
          >
            Create Free Account
          </button>

          <button
            onClick={handleSignIn}
            className="w-full py-3 border border-rdc-warm-gray text-rdc-dark-gray font-medium rounded-lg hover:bg-rdc-cream transition-colors"
          >
            Already have an account? Sign In
          </button>

          <p className="text-xs text-center text-rdc-taupe mt-4">
            Free forever • No credit card required
          </p>
        </div>
      </div>
    </div>
  )
}

// Hook for easy use throughout the app
import { createContext, useContext } from 'react'

const SignUpPromptContext = createContext(null)

export const useSignUpPrompt = () => {
  const context = useContext(SignUpPromptContext)
  if (!context) {
    // Return a no-op if used outside provider (for authenticated users)
    return { showPrompt: () => {}, isGuest: false }
  }
  return context
}

export const SignUpPromptProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [action, setAction] = useState('continue')

  const showPrompt = (actionText = 'continue') => {
    setAction(actionText)
    setIsOpen(true)
  }

  const hidePrompt = () => {
    setIsOpen(false)
  }

  return (
    <SignUpPromptContext.Provider value={{ showPrompt, hidePrompt, isGuest: true }}>
      {children}
      <SignUpPrompt isOpen={isOpen} onClose={hidePrompt} action={action} />
    </SignUpPromptContext.Provider>
  )
}
